import React, { useEffect, useState } from 'react';
import { loadIncidentLogs } from '../../app/resolvers';
import type { Incident } from '../../lib/incident';
import RemediationsPanel from '../RemediationsPanel';
import type { S3LogEntry } from '../../types/logs';

interface MemberAgentProps {
  incident: Incident;
  /** The agent finished a remediation: read the records again. */
  onDone: () => void;
}

/**
 * The remediation agent for one incident of a campaign, as the incident screen used to run it: it reads the
 * incident's logs, asks the agent for a plan and runs it step by step. It exists only in Live (Demo has no agent)
 * and is mounted only when its section is opened, so the logs are not read for nothing. The panel is still written
 * for a dark surface, so it sits on one.
 */
const MemberAgent: React.FC<MemberAgentProps> = ({ incident, onDone }) => {
  const s3Uri = incident.raw?.s3_uri as string | undefined;
  const [logs, setLogs] = useState<S3LogEntry[]>([]);
  const [loading, setLoading] = useState(Boolean(s3Uri));

  useEffect(() => {
    if (!s3Uri) {
      setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);
    loadIncidentLogs(s3Uri)
      .then((lines) => {
        if (alive) setLogs(lines as S3LogEntry[]);
      })
      .catch(() => undefined)
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [s3Uri]);

  return (
    <div data-theme="normal" className="c3-camp-agent">
      <RemediationsPanel
        threatEventId={incident.id}
        s3Logs={logs}
        s3LogsLoading={loading}
        threatClassification={incident.type}
        onRemediationComplete={onDone}
      />
    </div>
  );
};

export default MemberAgent;
